import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useForm, FormProvider } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import { GetServerSideProps } from 'next';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as Fathom from 'fathom-client';

import Head from '@/components/Head';
import ProgressBar from '@/components/ProgressBar';
import Loading from '@/components/Loading';
import RenewalOldDetails from '@/components/steps/RenewalOldDetails';
import PersonalDetails from '@/components/steps/PersonalDetails';
import AddressDetails from '@/components/steps/AddressDetails';
import SecurityDetails from '@/components/steps/SecurityDetails';
import Confirmation from '@/components/steps/Confirmation';
import { renewalOldSchema } from '@/utils/schemas/renewalOld';
import PriceInfo from '@/components/PriceInfo';
import {
  checkIfSubscriptionTheme,
  getTheme,
  getSubscriptionProduct,
  saveApplication,
  saveSubscription,
  sendGTagSubmitEvent,
  editApplication,
} from '@/utils/helpers';
import { useTheme } from '@/contexts/ThemeContext';
import { useApplication } from '@/hooks/useApplication';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';

export default function RenewalOld(): JSX.Element {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [customerId, setCustomerId] = useState<string>(null);
  const theme = useTheme();
  const { CustomEvent } = usePiwikPro();
  const { landingPage } = theme;
  const {
    state: {
      form: { licenceNumber, nin },
    },
  } = useStateMachine();
  const formMethods = useForm<any>({
    context: { customerId },
    resolver: yupResolver(renewalOldSchema),
    mode: 'onBlur',
    shouldUnregister: false,
    defaultValues: {
      driver_number: licenceNumber,
      ni_number: nin,
    },
  });
  const [tenancyPeriods, setTenancyPeriods] = useState<{
    [key: string]: number;
  }>({ current: 0 });
  const isDlAssistant = landingPage === 'v12' || landingPage === 'v13';

  const {
    handleSubmit,
    register,
    watch,
    trigger,
    errors,
    setError,
    clearErrors,
    setValue,
  } = formMethods;
  const { applicationId } = useApplication(setValue);
  const email = watch('email');
  const priority = watch('priority');
  const consent = watch('consent');
  const isDisabled =
    (!!customerId || email?.length > 0 ? false : true) || !consent;
  const TITLE = process.env.NEXT_PUBLIC_TITLE;
  const RENEWAL_OLD = process.env.NEXT_PUBLIC_RENEWAL_OLD;
  const DL_FAST_TRACK = process.env.NEXT_PUBLIC_DL_FAST_TRACK;
  const IS_BANK = process.env.NEXT_PUBLIC_IS_BANK === 'true';
  const isOlyaTheme = ['oa', 'oa2', 'oa3'].includes(theme.slug);
  const isSubscriptionTheme = checkIfSubscriptionTheme(
    theme.slug,
    theme.landingPage
  );

  const SUBSCRIPTION = getSubscriptionProduct(theme.landingPage);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const customerId = localStorage.getItem('customerId');
      setCustomerId(customerId);
    }
  }, []);

  const submitApplication = (e) => {
    e.preventDefault();
    const isCloackingEnabled = theme.cloackingEnabled;
    const products: string[] = [];
    if (isSubscriptionTheme) {
      products.push(`${RENEWAL_OLD}_no_fees`, SUBSCRIPTION);
    } else {
      products.push(RENEWAL_OLD);
    }
    if (priority) products.push(DL_FAST_TRACK);

    handleSubmit(async (application) => {
      if (!isSubscriptionTheme) {
        saveApplication(
          products,
          application,
          setIsLoading,
          router,
          customerId,
          isCloackingEnabled
        );
      } else {
        applicationId
          ? editApplication({
              application,
              applicationId,
              products,
              router,
            })
          : saveSubscription({
              products,
              application,
              setIsLoading,
              router,
              customerId,
              isCloackingEnabled,
            });
      }

      // Analytics for Olya Themes
      if (isOlyaTheme) {
        Fathom.trackGoal('1GTXT8TX', 0);
        sendGTagSubmitEvent('submit_application');
        CustomEvent.trackEvent('Form', 'submit_application');
      }
    })(e);
  };

  const nextStep = () => {
    setStep(step + 1);
  };

  const previousStep = () => {
    setStep(step - 1);
    clearErrors();
  };

  const validateAndNext = async () => {
    await trigger();
    await validateTenancy();
    if (Object.keys(errors).length === 0) nextStep();
    window.scrollTo(0, 150);
  };

  const validateTenancy = async () => {
    const totalTenanacy = Object.keys(tenancyPeriods).reduce(
      (sum, key) => sum + tenancyPeriods[key],
      0
    );

    if (step === 3 && totalTenanacy > 0 && totalTenanacy < 36) {
      setError('tenancyPeriod', {
        type: 'manual',
        message: 'You must complete the full 3 years address history',
      });
    } else {
      clearErrors('tenancyPeriod');
    }
    await trigger();
  };

  const title = watch('title');

  useEffect(() => {
    if (title) {
      (async () => {
        await trigger();
      })();
    }
  }, [title]);

  return (
    <>
      <Head title={`${TITLE} - Renewal Old`} />
      <div
        className={` max-w-8xl mx-auto py-16 px-4 sm:px-6 lg:px-8 min-h-screen ${
          isDlAssistant ? 'bg-white' : 'bg-cool-gray-50'
        } `}
      >
        <h2
          className={`text-2xl text-center leading-8 font-bold tracking-tight uppercase text-gray-900 flex justify-center md:mb-10 ${
            isDlAssistant && 'py-12'
          }`}
        >
          Renew your driver’s licence if you are 70 or over
        </h2>
        <ProgressBar
          landingPage={landingPage}
          variant={theme.progressBar}
          step={step}
        />
        <div className="md:flex md:space-x-4 space-y-4 md:space-y-0">
          <div
            className={
              isDlAssistant
                ? 'bg-gray-100 w-full'
                : 'bg-box-background border-2 border-box-border w-full'
            }
          >
            <FormProvider {...formMethods}>
              <form method="post" onSubmit={submitApplication}>
                <input type="hidden" name="step" value={step} ref={register} />
                <input
                  type="hidden"
                  name="form_type"
                  value="renewal-old"
                  ref={register}
                />

                <div className={step === 1 ? 'block' : 'hidden'}>
                  <RenewalOldDetails />
                </div>
                <div className={step === 2 ? 'block' : 'hidden'}>
                  <PersonalDetails skipYears={69} />
                </div>
                <div className={step === 3 ? 'block' : 'hidden'}>
                  <AddressDetails
                    tenancyPeriods={tenancyPeriods}
                    setTenancyPeriods={setTenancyPeriods}
                  />
                </div>
                <div className={step === 4 ? 'block' : 'hidden'}>
                  <SecurityDetails />
                </div>
                <div className={step === 5 ? 'block' : 'hidden'}>
                  <Confirmation slug={RENEWAL_OLD} />
                </div>

                <div className="w-full mt-8 flex items-center justify-between px-6 py-6">
                  <div>
                    <input
                      type="button"
                      className={`w-full flex items-center justify-center py-3 bg-transparent border border-transparent text-sm leading-6 font-medium uppercase rounded-md ${
                        isDlAssistant
                          ? 'text-red-500 border-1 border-red-500 px-8'
                          : 'text-gray-400  hover:text-gray-900'
                      }  cursor-pointer focus:outline-none transition duration-150 ease-in-out md:text-base md:py-2 ${
                        step > 1 ? 'block' : 'hidden'
                      }`}
                      value="Back"
                      onClick={previousStep}
                    />
                  </div>

                  <div>
                    <div className="rounded-md shadow">
                      <button
                        disabled={isDisabled}
                        type="submit"
                        className={`w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm leading-6 font-medium uppercase rounded-md text-white ${
                          isDlAssistant
                            ? 'bg-red-500 hover:bg-red-600'
                            : 'bg-primary hover:bg-primary-dark'
                        }  cursor-pointer focus:outline-none focus:bg-primary-dark focus:shadow-outline-indigo transition duration-150 ease-in-out md:py-2 md:px-8 disabled:cursor-not-allowed disabled:bg-gray-400 md:text-base ${
                          step === 5 ? 'block' : 'hidden'
                        }`}
                      >
                        Submit Application
                      </button>
                    </div>

                    <div className="rounded-md shadow">
                      <input
                        type="button"
                        className={`w-full flex items-center justify-center px-8 py-3 border border-transparent text-base leading-6 font-medium uppercase rounded-md text-white ${
                          isDlAssistant
                            ? 'bg-red-500 hover:bg-red-600'
                            : 'bg-primary hover:bg-primary-dark'
                        } cursor-pointer  focus:outline-none focus:bg-primary-dark focus:shadow-outline-indigo transition duration-150 ease-in-out md:py-2 md:px-8 ${
                          step < 5 ? 'block' : 'hidden'
                        }`}
                        onClick={validateAndNext}
                        value="Next"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
          {IS_BANK && <PriceInfo productSlug={RENEWAL_OLD} />}
        </div>
      </div>
      {isLoading ? <Loading /> : null}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  return getTheme(context);
};
